import React, { useMemo, useState } from "react";
import { Button, Card, Col, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import useCuration from "../apis/Curation";

function parseInfo(info) {
  if (typeof info !== "string") return info;
  return JSON.parse(info.split("'").join("\""));
}

function buildImageSrc(item) {
  const baseDate = item.info.reportDate || item.info.lastReportDate || item.pubdate;
  const year = String(baseDate).slice(0, 4);
  const month = String(baseDate).slice(5, 7);
  const selectedDocumentId = item.info.documentId || item.info.lastDocumentId;
  const transactionCode = item.seltype === "recentInsiderAmountHighSell" || item.seltype === "recentInsiderFrequentSell" ? "S" : "P";
  return `https://whisperj.s3.ap-northeast-2.amazonaws.com/image/${year}/${month}/${selectedDocumentId}_${item.ticker}_${transactionCode}.png`;
}

const buySliderType = "recentInsiderAmountHighBuy";
const sellSliderType = "recentInsiderAmountHighSell";
const frequentSellSliderType = "recentInsiderFrequentSell";
const listSections = [
  { key: "recentInsider10PercentBuy", label: "대주주 매수" },
  { key: "recentInsiderDirectorBuy", label: "디렉터 매수" },
  { key: "recentInsiderOfficerBuy", label: "임원 매수" }
];

export default function InsiderNews() {
  const { data: curationItems = [], isError, isLoading } = useCuration();
  const navigate = useNavigate();
  const [openedSectionKey, setOpenedSectionKey] = useState(null);
  const [showListModal, setShowListModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [returnSectionKey, setReturnSectionKey] = useState(null);
  const [buyActiveIndex, setBuyActiveIndex] = useState(0);
  const [sellActiveIndex, setSellActiveIndex] = useState(0);
  const [frequentSellActiveIndex, setFrequentSellActiveIndex] = useState(0);

  const insiderItems = useMemo(() => {
    return curationItems.map((item) => ({
      ...item,
      info: parseInfo(item.info)
    }));
  }, [curationItems]);

  const buySliderItems = useMemo(() => {
    return insiderItems
      .filter((item) => item.seltype === buySliderType)
      .sort((a, b) => a.rank - b.rank)
      .map((item) => ({
        ...item,
        reporter: item.info.reporterName || item.info.lastReporter,
        reportDate: item.info.reportDate || item.info.lastReportDate,
        imageSrc: buildImageSrc(item)
      }));
  }, [insiderItems]);

  const sellSliderItems = useMemo(() => {
    return insiderItems
      .filter((item) => item.seltype === sellSliderType)
      .sort((a, b) => a.rank - b.rank)
      .map((item) => ({
        ...item,
        reporter: item.info.reporterName || item.info.lastReporter,
        reportDate: item.info.reportDate || item.info.lastReportDate,
        imageSrc: buildImageSrc(item)
      }));
  }, [insiderItems]);

  const frequentSellSliderItems = useMemo(() => {
    return insiderItems
      .filter((item) => item.seltype === frequentSellSliderType)
      .sort((a, b) => a.rank - b.rank)
      .map((item) => ({
        ...item,
        reporter: item.info.reporterName || item.info.lastReporter,
        reportDate: item.info.reportDate || item.info.lastReportDate,
        imageSrc: buildImageSrc(item)
      }));
  }, [insiderItems]);

  const secondRowCards = useMemo(() => {
    return listSections.map((section) => ({
      ...section,
      allItems: insiderItems
        .filter((item) => item.seltype === section.key)
        .sort((a, b) => a.rank - b.rank)
        .map((item) => ({
          ...item,
          reporter: item.info.reporterName || item.info.lastReporter,
          reportDate: item.info.reportDate || item.info.lastReportDate,
          imageSrc: buildImageSrc(item)
        }))
    }));
  }, [insiderItems]);

  const openedSection = useMemo(() => {
    return secondRowCards.find((section) => section.key === openedSectionKey) || null;
  }, [openedSectionKey, secondRowCards]);

  const shouldLoadSlideImage = (index, activeIndex, length) => {
    if (length <= 4) return true;
    const visibleIndexes = [
      activeIndex % length,
      (activeIndex + 1) % length,
      (activeIndex + 2) % length,
      (activeIndex + 3) % length
    ];

    return visibleIndexes.includes(index);
  };

  const handleTickerMove = (ticker) => {
    if (!ticker) return;
    setShowImageModal(false);
    setReturnSectionKey(null);
    navigate(`/stock/${ticker}`);
  };

  return (
    <>
      <div style={{ margin: "0 auto", maxWidth: "1120px" }}>
        <Row>
          <Col style={{ margin: "13px 0" }}>
            <Card>
              <Card.Header className="border-0 bg-transparent pb-0">
                <div className="home-section-header">
                  <div>
                    <div className="home-section-title">임원들의 주식 매수 동향</div>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="px-2 px-lg-2">
                {isLoading ? (
                  <div>loading...</div>
                ) : isError ? (
                  <div>error</div>
                ) : (
                  <div>
                    <div className="home-section-title mb-3">최근 내부자들의 고액 매수 종목을 확인해보세요!</div>
                    <Swiper
                      modules={[Autoplay]}
                      spaceBetween={12}
                      slidesPerView={1}
                      slidesPerGroup={1}
                      onSlideChange={(swiper) => setBuyActiveIndex(swiper.realIndex)}
                      loop={buySliderItems.length > 3}
                      autoplay={buySliderItems.length > 3 ? {
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                      } : false}
                      breakpoints={{
                        768: {
                          slidesPerView: 2,
                          slidesPerGroup: 2
                        },
                        992: {
                          slidesPerView: 3,
                          slidesPerGroup: 3
                        }
                      }}
                    >
                      {buySliderItems.map((item, index) => (
                        <SwiperSlide key={`${item.seltype}-${item.ticker}-${item.rank}`}>
                          <Card
                            className="h-100"
                            style={{ cursor: "pointer" }}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setSelectedItem(item);
                              setReturnSectionKey(null);
                              setShowImageModal(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedItem(item);
                                setReturnSectionKey(null);
                                setShowImageModal(true);
                              }
                            }}
                          >
                            <Card.Body className="p-2">
                              <div
                                style={{
                                  height: 520,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "0.75rem",
                                  padding: 12
                                }}
                              >
                                {shouldLoadSlideImage(index, buyActiveIndex, buySliderItems.length) ? (
                                  <img
                                    src={item.imageSrc}
                                    alt={item.ticker}
                                    loading="lazy"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                      display: "block"
                                    }}
                                  />
                                ) : null}
                              </div>
                            </Card.Body>
                            <Card.Footer>
                              <div className="fw-semibold">{item.companyKor} ({item.ticker})</div>
                              <div className="text-secondary fs-sm">{item.pubdate}</div>
                            </Card.Footer>
                          </Card>
                        </SwiperSlide>
                      ))}
                    </Swiper>

                  </div>
                )}
              </Card.Body>
              <Row className="g-3 px-2 px-lg-2 pb-4">
                {secondRowCards.map((section) => (
                  <Col key={section.key} xs="12" md="6" lg="4">
                    <Card className="h-100">
                      <Card.Header
                        className="d-flex align-items-center justify-content-between"
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setOpenedSectionKey(section.key);
                          setShowListModal(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setOpenedSectionKey(section.key);
                            setShowListModal(true);
                          }
                        }}
                      >
                        <span className="fw-semibold">{section.label}</span>
                        <span className="text-secondary fs-xs">{section.allItems.length}건</span>
                      </Card.Header>
                      <Card.Body className="p-0">
                        {section.allItems.slice(0, 5).map((item, index) => {
                          const shares = Number(item.info.transactionShares);
                          const price = Number(item.info.pricePerShare);
                          const postTransaction = Number(item.info.postTransaction);

                          return (
                            <div
                              key={`${section.key}-${item.ticker}-${item.rank}`}
                              className={`px-3 py-2 ${index === Math.min(section.allItems.length, 5) - 1 ? "" : "border-bottom"}`}
                              style={{
                                height: 148,
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column"
                              }}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setSelectedItem(item);
                                setReturnSectionKey(section.key);
                                setShowImageModal(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setSelectedItem(item);
                                  setReturnSectionKey(section.key);
                                  setShowImageModal(true);
                                }
                              }}
                            >
                              <div
                                className="fw-semibold"
                                style={{
                                  lineHeight: 1.4,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden"
                                }}
                              >
                                {item.companyKor} ({item.ticker})
                              </div>
                              <div
                                className="text-secondary mb-1"
                                style={{
                                  fontSize: "0.9rem",
                                  minHeight: 21,
                                  lineHeight: 1.4,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden"
                                }}
                              >
                                {item.reporter || ""}
                              </div>
                              <div
                                className="text-secondary mb-1"
                                style={{
                                  fontSize: "0.92rem",
                                  lineHeight: 1.5,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  minHeight: 44
                                }}
                              >
                                {shares ? `${shares.toLocaleString()}주를 매수했습니다. ` : ""}
                                {price ? `주당 평균 금액은 $${price.toFixed(2)}로 알려졌습니다. ` : ""}
                                {postTransaction ? `거래 후 보유 주식수는 ${postTransaction.toLocaleString()}주입니다.` : ""}
                              </div>
                              <div className="d-flex justify-content-between text-secondary fs-xs mt-auto">
                                <span>발행일: {item.pubdate}</span>
                                <span>보고일: {item.reportDate || "-"}</span>
                              </div>
                            </div>
                          );
                        })}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card.Body className="px-2 px-lg-2 pt-0 pb-4">
                {isLoading ? null : isError ? null : (
                  <div>
                    <div className="home-section-title mt-2 mb-3">최근 내부자들의 고액 매도 종목을 확인해보세요!</div>
                    <Swiper
                      modules={[Autoplay]}
                      spaceBetween={12}
                      slidesPerView={1}
                      slidesPerGroup={1}
                      onSlideChange={(swiper) => setSellActiveIndex(swiper.realIndex)}
                      loop={sellSliderItems.length > 3}
                      autoplay={sellSliderItems.length > 3 ? {
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                      } : false}
                      breakpoints={{
                        768: {
                          slidesPerView: 2,
                          slidesPerGroup: 2
                        },
                        992: {
                          slidesPerView: 3,
                          slidesPerGroup: 3
                        }
                      }}
                    >
                      {sellSliderItems.map((item, index) => (
                        <SwiperSlide key={`${item.seltype}-${item.ticker}-${item.rank}`}>
                          <Card
                            className="h-100"
                            style={{ cursor: "pointer" }}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setSelectedItem(item);
                              setReturnSectionKey(null);
                              setShowImageModal(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedItem(item);
                                setReturnSectionKey(null);
                                setShowImageModal(true);
                              }
                            }}
                          >
                            <Card.Body className="p-2">
                              <div
                                style={{
                                  height: 520,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "0.75rem",
                                  padding: 12
                                }}
                              >
                                {shouldLoadSlideImage(index, sellActiveIndex, sellSliderItems.length) ? (
                                  <img
                                    src={item.imageSrc}
                                    alt={item.ticker}
                                    loading="lazy"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                      display: "block"
                                    }}
                                  />
                                ) : null}
                              </div>
                            </Card.Body>
                            <Card.Footer>
                              <div className="fw-semibold">{item.companyKor} ({item.ticker})</div>
                              <div className="text-secondary fs-sm">{item.pubdate}</div>
                            </Card.Footer>
                          </Card>
                        </SwiperSlide>
                      ))}
                    </Swiper>

                    <div className="home-section-title mt-4 mb-3">최근 내부자들의 집중 매도 종목을 확인해보세요!</div>
                    <Swiper
                      modules={[Autoplay]}
                      spaceBetween={12}
                      slidesPerView={1}
                      slidesPerGroup={1}
                      onSlideChange={(swiper) => setFrequentSellActiveIndex(swiper.realIndex)}
                      loop={frequentSellSliderItems.length > 3}
                      autoplay={frequentSellSliderItems.length > 3 ? {
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                      } : false}
                      breakpoints={{
                        768: {
                          slidesPerView: 2,
                          slidesPerGroup: 2
                        },
                        992: {
                          slidesPerView: 3,
                          slidesPerGroup: 3
                        }
                      }}
                    >
                      {frequentSellSliderItems.map((item, index) => (
                        <SwiperSlide key={`${item.seltype}-${item.ticker}-${item.rank}`}>
                          <Card
                            className="h-100"
                            style={{ cursor: "pointer" }}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setSelectedItem(item);
                              setReturnSectionKey(null);
                              setShowImageModal(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedItem(item);
                                setReturnSectionKey(null);
                                setShowImageModal(true);
                              }
                            }}
                          >
                            <Card.Body className="p-2">
                              <div
                                style={{
                                  height: 520,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "0.75rem",
                                  padding: 12
                                }}
                              >
                                {shouldLoadSlideImage(index, frequentSellActiveIndex, frequentSellSliderItems.length) ? (
                                  <img
                                    src={item.imageSrc}
                                    alt={item.ticker}
                                    loading="lazy"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                      display: "block"
                                    }}
                                  />
                                ) : null}
                              </div>
                            </Card.Body>
                            <Card.Footer>
                              <div className="fw-semibold">{item.companyKor} ({item.ticker})</div>
                              <div className="text-secondary fs-sm">{item.pubdate}</div>
                            </Card.Footer>
                          </Card>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal show={showListModal} onHide={() => setShowListModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: 700 }}>{openedSection?.label || "내부자 뉴스"}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {openedSection?.allItems?.length ? (
            openedSection.allItems.map((item, index) => {
              const shares = Number(item.info.transactionShares);
              const price = Number(item.info.pricePerShare);
              const postTransaction = Number(item.info.postTransaction);

              return (
                <div
                  key={`${openedSection.key}-${item.ticker}-${item.rank}-${index}`}
                  className="py-2 border-bottom"
                  style={{
                    cursor: "pointer",
                    minHeight: 132,
                    display: "flex",
                    flexDirection: "column"
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelectedItem(item);
                    setReturnSectionKey(openedSection.key);
                    setShowListModal(false);
                    setShowImageModal(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedItem(item);
                      setReturnSectionKey(openedSection.key);
                      setShowListModal(false);
                      setShowImageModal(true);
                    }
                  }}
                >
                  <div
                    className="fw-semibold"
                    style={{
                      lineHeight: 1.4,
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden"
                    }}
                  >
                    {item.companyKor} ({item.ticker})
                  </div>
                  <div
                    className="text-secondary mb-1"
                    style={{
                      fontSize: "0.9rem",
                      minHeight: 21,
                      lineHeight: 1.4,
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden"
                    }}
                  >
                    {item.reporter || ""}
                  </div>
                  <div
                    className="text-secondary mb-1"
                    style={{
                      fontSize: "0.92rem",
                      lineHeight: 1.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      minHeight: 44
                    }}
                  >
                    {shares ? `${shares.toLocaleString()}주를 매수했습니다. ` : ""}
                    {price ? `주당 평균 금액은 $${price.toFixed(2)}로 알려졌습니다. ` : ""}
                    {postTransaction ? `거래 후 보유 주식수는 ${postTransaction.toLocaleString()}주입니다.` : ""}
                  </div>
                  <div className="d-flex justify-content-between text-secondary fs-xs mt-auto">
                    <span>발행일: {item.pubdate}</span>
                    <span>보고일: {item.reportDate || "-"}</span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-secondary">표시할 내부자 뉴스가 없습니다.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowListModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showImageModal}
        onHide={() => {
          setShowImageModal(false);
          setReturnSectionKey(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedItem ? (
              <div>
                <div
                  role="button"
                  tabIndex={0}
                  className="fw-semibold"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleTickerMove(selectedItem.ticker)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleTickerMove(selectedItem.ticker);
                    }
                  }}
                >
                  {selectedItem.companyKor} ({selectedItem.ticker})
                </div>
                <div className="text-secondary fs-sm fw-normal">
                  {selectedItem.reporter || "-"} · {selectedItem.reportDate || "-"}
                </div>
              </div>
            ) : "이미지 상세"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem ? (
            <img
              src={selectedItem.imageSrc}
              alt={selectedItem.ticker}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                backgroundColor: "#f8f9fa",
                borderRadius: "0.75rem"
              }}
            />
          ) : (
            <div className="text-secondary">항목을 선택해 주세요.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {returnSectionKey ? (
            <Button
              variant="outline-primary"
              onClick={() => {
                setOpenedSectionKey(returnSectionKey);
                setShowImageModal(false);
                setShowListModal(true);
              }}
            >
              목록으로
            </Button>
          ) : null}
          <Button variant="outline-secondary" onClick={() => setShowImageModal(false)}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
